// MyTrips — landing page with all user trips (Booking-style tabs).
// On first login, seeds the current "Europa Mayo 2026" trip from local data.
import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, LogOut, Plane, Calendar, Share2, User, Sparkles } from "lucide-react";
import { useAuth, api } from "../auth/AuthContext";
import { TRIP } from "../data/trip";
import { hasPin, hasDeviceKey, setDeviceKey } from "../auth/pinStore";
import PinLock from "./PinLock";
import ShareSheet from "../components/ShareSheet";
import InstallBanner from "../components/InstallBanner";

const SEEDED_KEY = "trip-seeded-v1";

function statusOf(t) {
  const today = new Date().toISOString().slice(0, 10);
  if (t.start_date > today) return { label: "Próximo", color: "hsl(var(--accent))", bucket: "programados" };
  if (t.end_date < today) return { label: "Finalizado", color: "hsl(var(--muted))", bucket: "pasados" };
  return { label: "En curso", color: "hsl(140 60% 40%)", bucket: "activos" };
}

function daysBetween(a, b) {
  const ms = new Date(b) - new Date(a);
  return Math.max(1, Math.round(ms / (1000 * 60 * 60 * 24)) + 1);
}

function formatRange(start, end) {
  const opts = { day: "numeric", month: "short" };
  const s = new Date(start).toLocaleDateString("es-CL", opts);
  const e = new Date(end).toLocaleDateString("es-CL", { ...opts, year: "numeric" });
  return `${s} → ${e}`;
}

function TripsTabs({ tab, setTab, trips }) {
  const counts = useMemo(() => {
    const c = { activos: 0, programados: 0, pasados: 0 };
    trips.forEach((t) => { c[statusOf(t).bucket] += 1; });
    return c;
  }, [trips]);
  const tabs = [
    { key: "activos", label: "Activos" },
    { key: "programados", label: "Programados" },
    { key: "pasados", label: "Pasados" },
  ];
  return (
    <div className="flex items-center gap-1.5 mt-5 mb-4 overflow-x-auto no-scrollbar" role="tablist">
      {tabs.map((t) => {
        const active = tab === t.key;
        return (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
              active
                ? "bg-[hsl(var(--accent))] text-white border-transparent"
                : "bg-transparent text-[hsl(var(--ink-soft))] border-[hsl(var(--line))]"
            }`}
            role="tab"
            aria-selected={active}
            data-testid={`trips-tab-${t.key}`}
          >
            {t.label}
            {counts[t.key] > 0 && (
              <span className={`ml-1.5 text-[10px] font-bold ${active ? "opacity-80" : "opacity-60"}`}>
                {counts[t.key]}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function MyTrips() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(true);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [tab, setTab] = useState("activos"); // activos | programados | pasados
  const [shareTrip, setShareTrip] = useState(null);
  const seededRef = useRef(false);

  // First time user with no PIN: prompt setup once.
  useEffect(() => {
    if (user && !hasPin() && !localStorage.getItem("pin-setup-skipped")) {
      setShowPinSetup(true);
    }
  }, [user]);

  // Auto-enroll device: if user has local PIN but no server-side device_key,
  // prompt to re-enter PIN once to register it on the server. This avoids
  // needing Google login on every reopen (critical for iOS standalone PWA).
  useEffect(() => {
    if (!user || !hasPin() || hasDeviceKey()) return;
    // Ask the user once. We need plain PIN to register on server (we only stored hash).
    // Simplest UX: silently show a small prompt next time.
    const askedKey = "pin-enrolled-asked-v1";
    if (localStorage.getItem(askedKey)) return;
    // Prompt user
    const pin = window.prompt(
      "Para evitar que te vuelva a pedir Google, escribe tu PIN una vez. " +
      "Lo voy a registrar en el servidor para que el PIN te entre directo siempre."
    );
    localStorage.setItem(askedKey, "1");
    if (!pin || pin.length < 4) return;
    api("/api/auth/pin/register", { method: "POST", body: JSON.stringify({ pin }) })
      .then((r) => { if (r.device_key) setDeviceKey(r.device_key); })
      .catch(() => { /* ignore — user can retry later */ });
  }, [user]);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        let list = await api("/api/trips");
        // Seed on first login: create the current trip if user has none.
        if (list.length === 0 && !localStorage.getItem(SEEDED_KEY) && !seededRef.current) {
          seededRef.current = true;
          // Unique cities preserving first appearance order
          const seen = new Set();
          const cities = [];
          (TRIP.days || []).forEach((d) => {
            (d.city || "").split(/[→·]/).map((c) => c.trim()).filter(Boolean).forEach((c) => {
              if (!seen.has(c)) { seen.add(c); cities.push(c); }
            });
          });
          await api("/api/trips", {
            method: "POST",
            body: JSON.stringify({
              title: "Europa + Israel 2026",
              subtitle: "Madrid · Budapest · Praga · Tel Aviv",
              start_date: "2026-05-31",
              end_date: "2026-06-15",
              cities,
              hero_image: "https://images.unsplash.com/photo-1541849546-216549ae216d?w=1200&q=70",
              data: TRIP,
            }),
          });
          localStorage.setItem(SEEDED_KEY, "1");
          list = await api("/api/trips");
        }
        setTrips(list);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingTrips(false);
      }
    })();
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-sm text-[hsl(var(--muted))]">Cargando…</p>
      </div>
    );
  }
  if (!user) return null;

  if (showPinSetup) {
    return (
      <PinLock
        mode="set"
        hint="Crea un PIN para entrar más rápido la próxima vez (en lugar de Google). Solo en este dispositivo."
        onSuccess={() => setShowPinSetup(false)}
        onSkip={() => {
          localStorage.setItem("pin-setup-skipped", "1");
          setShowPinSetup(false);
        }}
      />
    );
  }

  return (
    <div className="container-app pt-6 pb-10" data-testid="my-trips-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-1">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))] font-semibold">Hola{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</p>
          <h1 className="text-3xl font-bold leading-tight">Mis viajes</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/perfil")}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] active:scale-95"
            aria-label="Mi perfil"
            data-testid="profile-btn"
          >
            <User size={16} />
          </button>
          <button
            onClick={logout}
            className="w-9 h-9 rounded-full flex items-center justify-center bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))] text-[hsl(var(--ink-soft))] active:scale-95"
            aria-label="Cerrar sesión"
            data-testid="logout-btn"
          >
            <LogOut size={16} />
          </button>
        </div>
      </div>

      {/* PWA install banner */}
      <InstallBanner />

      {/* Tabs: Activos · Programados · Pasados */}
      <TripsTabs tab={tab} setTab={setTab} trips={trips} />

      {/* New trip card — routes to AI wizard */}
      <button
        onClick={() => navigate("/crear-viaje")}
        className="w-full flex items-center gap-3 mt-6 mb-5 p-4 rounded-2xl border-2 border-dashed border-[hsl(var(--accent))]/40 text-[hsl(var(--ink-soft))] active:scale-[0.99] transition-all hover:bg-[hsl(var(--bg-card))]"
        data-testid="new-trip-btn"
      >
        <div className="w-10 h-10 rounded-full bg-[hsl(var(--accent))] text-white flex items-center justify-center shrink-0">
          <Sparkles size={18} />
        </div>
        <div className="text-left">
          <p className="text-sm font-semibold">Curar un nuevo viaje con IA</p>
          <p className="text-xs text-[hsl(var(--muted))]">Sube tus reservas o cuéntanos qué ciudades — la IA arma el itinerario cultural</p>
        </div>
      </button>

      {/* Trips list */}
      {loadingTrips && (
        <p className="text-center text-sm text-[hsl(var(--muted))] py-12">Cargando…</p>
      )}
      {!loadingTrips && trips.length === 0 && (
        <p className="text-center text-sm text-[hsl(var(--muted))] py-12">No tienes viajes todavía.</p>
      )}
      {!loadingTrips && trips.length > 0 && trips.filter((t) => statusOf(t).bucket === tab).length === 0 && (
        <p className="text-center text-sm text-[hsl(var(--muted))] py-12" data-testid="trips-empty-bucket">
          {tab === "activos" && "No hay viajes en curso ahora mismo."}
          {tab === "programados" && "Aún no tenés viajes próximos. Toca +Crear para armar uno."}
          {tab === "pasados" && "Tus viajes finalizados aparecerán acá."}
        </p>
      )}
      <ul className="space-y-3">
        {trips.filter((t) => statusOf(t).bucket === tab).map((t, i) => {
          const st = statusOf(t);
          const totalDays = daysBetween(t.start_date, t.end_date);
          return (
            <motion.li
              key={t.trip_id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.05 * i }}
              data-testid={`trip-card-${t.trip_id}`}
            >
              <Link
                to={`/viaje/${t.trip_id}`}
                className="block card-paper overflow-hidden active:scale-[0.99] transition-transform"
              >
                {/* Hero */}
                <div
                  className="h-32 relative"
                  style={{
                    backgroundImage: t.hero_image ? `url(${t.hero_image})` : "none",
                    backgroundColor: "hsl(var(--ink))",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                >
                  <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.65) 100%)" }}
                  />
                  <span
                    className="absolute top-3 left-3 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-white/90"
                    style={{ color: st.color }}
                  >
                    {st.label}
                  </span>
                  {/* Share button — más grande con texto, fácil de tocar */}
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShareTrip(t); }}
                    className="absolute top-3 right-3 px-3 h-9 rounded-full bg-white/95 backdrop-blur flex items-center gap-1.5 shadow-md active:scale-95 transition-transform"
                    aria-label="Compartir viaje"
                    data-testid={`trip-share-${t.trip_id}`}
                  >
                    <Share2 size={14} className="text-[hsl(var(--ink))]" />
                    <span className="text-xs font-semibold text-[hsl(var(--ink))]">Compartir</span>
                  </button>
                  <div className="absolute bottom-3 left-3 right-3 text-white">
                    <h3 className="text-lg font-bold leading-tight">{t.title}</h3>
                    {t.subtitle && (
                      <p className="text-xs opacity-90 mt-0.5">{t.subtitle}</p>
                    )}
                  </div>
                </div>
                {/* Body */}
                <div className="p-4">
                  <div className="flex items-center gap-3 text-xs text-[hsl(var(--ink-soft))]">
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={12} />
                      {formatRange(t.start_date, t.end_date)}
                    </span>
                    <span className="text-[hsl(var(--muted))]">·</span>
                    <span>{totalDays} días</span>
                  </div>
                  {t.cities && t.cities.length > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-[hsl(var(--muted))] mt-2 flex-wrap">
                      <Plane size={11} />
                      <span className="font-medium">{t.cities.join(" → ")}</span>
                    </div>
                  )}
                </div>
              </Link>
            </motion.li>
          );
        })}
      </ul>

      {/* Share sheet — global per-trip share modal */}
      <ShareSheet
        open={!!shareTrip}
        trip={shareTrip}
        onClose={() => {
          // refresh trips so share_token gets cached locally
          setShareTrip(null);
          api("/api/trips").then(setTrips).catch(() => {});
        }}
      />
    </div>
  );
}
